import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ENDPOINT_CONSTANTS } from '../../../shared/constants/endpoint.constants';
import { ChatbotStreamRequest } from '../interfaces/chatbot-stream-request.interface';
import { TenantService } from '../../../shared/services/tenant.service';

@Injectable({
  providedIn: 'root'
})
export class StreamingController {
  private readonly baseUrl = environment.pythonUrl;
  private readonly apiKey = environment.apiKey;
  private readonly useProxy = true; // Set to false if you want to bypass proxy

  constructor(private tenantService: TenantService) { }

  private constructUrl(endpoint: string): string {
    const baseUrl = this.baseUrl;
    const url = new URL(`${baseUrl}/${endpoint}`);
    return url.toString();
  }

  private appendQueryParams(url: string, params: Record<string, string | string[] | null | undefined>): string {
    const requestUrl = new URL(url);

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item !== null && item !== undefined && item !== '') {
            requestUrl.searchParams.append(key, item);
          }
        }
        return;
      }
      requestUrl.searchParams.set(key, value);
    });

    return requestUrl.toString();
  }

  getChatBotResponseStream(request: ChatbotStreamRequest): Observable<any> {
    return new Observable(observer => {
      const url = this.appendQueryParams(
        this.constructUrl(ENDPOINT_CONSTANTS.CHATBOT_TEST_STREAM),
        {
          query: request.query,
          user: request.user,
          chat_id: request.chat_id,
          user_id: request.user_id,
          timestamp: request.timestamp,
          tenant_name: request.tenant_name || this.tenantService.currentTenantName(),
          role: request.role,
          permissions: request.permissions ?? undefined
        }
      );

      let isStreaming = true;
      const abortController = new AbortController();
      let activeReader: ReadableStreamDefaultReader<Uint8Array> | null = null;

      fetch(url, {
        method: 'GET',
        headers: {
          'x-api-key': this.apiKey,
          'accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Connection': 'keep-alive'
        },
        signal: abortController.signal
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          if (!response.body) {
            throw new Error('No response body');
          }

          const reader = response.body.getReader();
          activeReader = reader;
          const decoder = new TextDecoder('utf-8');
          let buffer = '';

          const processText = async ({ done, value }: { done: boolean; value?: Uint8Array }): Promise<void> => {
            if (done || !isStreaming) {
              observer.complete();
              return;
            }

            try {
              const chunk = value ? decoder.decode(value, { stream: true }) : '';
              buffer += chunk;

              // Process each complete line (SSE format: data: {...})
              const lines = buffer.split(/\r?\n/);
              buffer = lines.pop() || '';

              for (const line of lines) {
                if (!line.trim() || !line.startsWith('data: ')) continue;

                try {
                  // Remove 'data: ' prefix and parse the JSON
                  const jsonStr = line.substring(6).trim();
                  if (!jsonStr) continue;

                  const data = JSON.parse(jsonStr);

                  if (data.type === 'event' && data.chunk) {
                    observer.next({
                      type: 'event',
                      chunk: data.chunk
                    });
                  }
                  if (data.type === 'plan' && data.text) {
                    observer.next({
                      type: 'plan',
                      text: data.text
                    });
                  }
                  if (data.type === 'toolstep' && data.call_id) {
                    observer.next({
                      type: 'toolstep',
                      call_id: data.call_id,
                      tool_name: data.tool_name,
                      status: data.status,
                      arguments: data.arguments,
                      result_summary: data.result_summary
                    });
                  }
                  if (data.type === 'content' && data.chunk) {
                    observer.next({
                      type: 'content',
                      chunk: data.chunk
                    });
                  } else if (data.type === 'metadata') {
                    observer.next({
                      type: 'metadata',
                      ...data
                    });
                  }
                  else if (data.type === 'ChatLable' && data.chunk) {
                    observer.next({ type: 'ChatLable', chunk: data.chunk });
                  } else if (data.type === 'sources' && data.chunk) {
                    observer.next({ type: 'sources', chunk: data.chunk });
                  } else if (data.type === 'done') {
                    observer.complete();
                    return;
                  }
                } catch (e) {
                  console.error('Error parsing stream data:', e, 'Data:', line);
                }
              }
            } catch (error) {
              console.error('Error in processText:', error);
              observer.error(error);
              return;
            }

            // Read the next chunk
            try {
              const nextChunk = await reader.read();
              return processText(nextChunk);
            } catch (error) {
              console.error('Error reading next chunk:', error);
              observer.error(error);
            }
          };

          // Start processing the stream
          reader.read().then(processText).catch(error => {
            if (!isStreaming || error?.name === 'AbortError') {
              return;
            }
            console.error('Error starting stream processing:', error);
            observer.error(error);
          });
        })
        .catch(error => {
          if (!isStreaming || error?.name === 'AbortError') {
            return;
          }
          console.error('Stream error:', error);
          observer.error(error);
        });

      // Cleanup function: actually tear down the network connection,
      // not just stop forwarding chunks to a detached subscriber.
      return () => {
        isStreaming = false;
        abortController.abort();
        activeReader?.cancel().catch(() => { });
      };
    });
  }
}
